import React from 'react'
import { FieldComponent } from './field-component'
import { FieldMount } from './mount-field'
import { renderArrayFields } from './render-array-fields'
import type {
  ArrayFieldConfig,
  BasicComponent,
  FieldConfig,
  GenericFieldConfig,
  GenericFormType,
  ParentField,
  SlotComponent,
  WithComponent,
  WithType,
} from './types'

interface FieldsRendererProps<T> {
  fields: GenericFieldConfig<T>
  form: GenericFormType<T>
  baseName?: string
  arrayIndex?: number
  parentField?: ParentField<T>
}

export const FieldsRenderer = <T,>({
  fields,
  form,
  baseName,
  arrayIndex,
  parentField,
}: FieldsRendererProps<T>) => (
  <>
    {fields.map((fieldConfig) => {
      const renderField = () => {
        if ((fieldConfig as BasicComponent<T>).type === 'basic') {
          return (
            <FieldMount
              Comp={(fieldConfig as BasicComponent<T>).component}
              props={{ form, arrayIndex }}
            />
          )
        }

        if ((fieldConfig as ArrayFieldConfig<T>).type === 'array')
          return renderArrayFields(fieldConfig as ArrayFieldConfig<T>, form, fields, baseName)

        const { type } = fieldConfig as WithComponent<T>
        if (
          (fieldConfig as WithType<T>).componentType ||
          type === 'custom' ||
          type === 'sub-item'
        ) {
          return (
            <FieldComponent
              fieldConfig={fieldConfig as FieldConfig<T>}
              form={form}
              arrayIndex={arrayIndex}
              baseName={baseName}
              parentField={parentField}
            />
          )
        }

        // Slot component with element
        if ((fieldConfig as SlotComponent<T>).element) {
          return (
            <FieldMount
              Comp={(fieldConfig as SlotComponent<T>).element as React.ElementType}
              props={{ form, arrayIndex, baseName }}
            />
          )
        }

        // Slot component with component
        return (fieldConfig as SlotComponent<T>).component
      }

      return <React.Fragment key={fieldConfig.name}>{renderField()}</React.Fragment>
    })}
  </>
)
